import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, MapPin } from 'lucide-react';
import api from '@/services/apiClient';
import useAuthStore from '@/store/authStore';
import Avatar from '@/components/ui/Avatar';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import { fmtMoney } from '@/lib/enums';
import { maskCpf, isValidCpfCnpj, onlyDigits } from '@/lib/masks';
import styles from '../public.module.css';

// Campos JSON podem chegar dupla-serializados de seeds antigos.
const asArray = (v) => {
  if (Array.isArray(v)) return v;
  try {
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Página pública do personal (link de bio) com checkout dos pacotes. */
export default function TrainerPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { user, isAuthenticated, login, register } = useAuthStore();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [buying, setBuying] = useState(null); // pacote no modal de checkout
  const [mode, setMode] = useState('register'); // register | login
  const [form, setForm] = useState({ name: '', email: '', password: '', cpf: '' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get(`/public/trainers/${slug}`)
      .then((res) => setData(res.data || res))
      .catch(() => setFailed(true));
  }, [slug]);

  useEffect(() => {
    if (data?.trainer?.name) document.title = `${data.trainer.name} · Movez`;
  }, [data]);

  const needsAccount = !isAuthenticated || user?.role !== 'student';
  const needsCpf = !needsAccount && !user?.cpfCnpj;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const specialties = useMemo(() => asArray(data?.trainer?.specialties), [data]);
  const certifications = useMemo(() => asArray(data?.trainer?.certifications), [data]);

  const checkout = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      let cpf = onlyDigits(form.cpf);
      if (needsAccount) {
        if (!isValidCpfCnpj(cpf)) throw new Error(t('publicPage.invalidCpf'));
        if (mode === 'register') {
          await register({ name: form.name, email: form.email, password: form.password, role: 'student', cpfCnpj: cpf });
        } else {
          const logged = await login(form.email, form.password);
          if (!logged.cpfCnpj) await api.patch('/auth/profile', { cpfCnpj: cpf });
        }
      } else if (needsCpf) {
        if (!isValidCpfCnpj(cpf)) throw new Error(t('publicPage.invalidCpf'));
        await api.patch('/auth/profile', { cpfCnpj: cpf });
      }
      // O serviço anexa o id da compra ao successUrl (redirect pós-pagamento).
      const res = await api.post(`/marketplace/${buying.id}/purchase`, {
        successUrl: `${window.location.origin}/compra`,
      });
      const out = res.data || res;
      const url = out.payment?.checkoutUrl;
      if (url) {
        window.location.href = url;
      } else {
        window.location.href = `/compra/${out.purchase.id}`;
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (failed) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.brand}>move<span>z</span></div>
          <EmptyState title={t('publicPage.notFound')} description={t('publicPage.notFoundDesc')} />
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className={styles.page}>
        <div className={styles.center}><Spinner /></div>
      </div>
    );
  }

  const { trainer, packages } = data;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>move<span>z</span></div>

        <div className={styles.header}>
          <Avatar name={trainer.name} src={trainer.avatar} size={84} />
          <div className={styles.name}>{trainer.name}</div>
          <div className={styles.meta}>
            {trainer.city && (<span><MapPin size={11} style={{ verticalAlign: -1 }} /> {trainer.city}</span>)}
            {trainer.ratingCount > 0 && (
              <span><Star size={11} style={{ verticalAlign: -1 }} /> {Number(trainer.ratingAvg).toFixed(1)} ({trainer.ratingCount})</span>
            )}
          </div>
          {trainer.bio && <p className={styles.bio}>{trainer.bio}</p>}
          {(specialties.length > 0 || certifications.length > 0) && (
            <div className={styles.chips}>
              {specialties.map((s) => <span key={s} className={styles.chip}>{s}</span>)}
              {certifications.map((c) => <span key={c} className={styles.chip}>{c}</span>)}
            </div>
          )}
        </div>

        <div className={styles.kicker}>{t('publicPage.packages')}</div>
        {packages.length === 0 ? (
          <p className={styles.hint}>{t('publicPage.noPackages')}</p>
        ) : (
          packages.map((p) => (
            <div key={p.id} className={styles.pkg}>
              {p.coverUrl && <img className={styles.pkgCover} src={p.coverUrl} alt="" />}
              <div className={styles.pkgBody}>
                <div className={styles.pkgTitle}>{p.title}</div>
                {p.description && <div className={styles.pkgDesc}>{p.description}</div>}
                <div className={styles.pkgFoot}>
                  <div>
                    <div className={styles.price}>{fmtMoney(p.price)}</div>
                    {p.durationDays && <div className={styles.duration}>{t('publicPage.duration', { days: p.durationDays })}</div>}
                  </div>
                  <Button onClick={() => { setBuying(p); setErr(null); }}>{t('publicPage.buy')}</Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal open={!!buying} onClose={() => setBuying(null)} kicker="Movez" title={buying?.title || ''} size="md">
        {buying && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div className={styles.hint}>
              {t('publicPage.checkoutIntro', { price: fmtMoney(buying.price), trainer: trainer.name })}
            </div>

            {needsAccount && (
              <>
                <div className={styles.tabs}>
                  <button type="button" className={`${styles.tab} ${mode === 'register' ? styles.tabActive : ''}`} onClick={() => setMode('register')}>
                    {t('publicPage.createAccount')}
                  </button>
                  <button type="button" className={`${styles.tab} ${mode === 'login' ? styles.tabActive : ''}`} onClick={() => setMode('login')}>
                    {t('publicPage.signIn')}
                  </button>
                </div>
                {mode === 'register' && <Input label={t('publicPage.name')} value={form.name} onChange={set('name')} />}
                <Input label={t('publicPage.email')} type="email" value={form.email} onChange={set('email')} />
                <Input label={t('publicPage.password')} type="password" value={form.password} onChange={set('password')} />
                <Input
                  label={t('publicPage.cpf')}
                  value={form.cpf}
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  onChange={(e) => setForm((f) => ({ ...f, cpf: maskCpf(e.target.value) }))}
                />
              </>
            )}

            {needsCpf && (
              <Input
                label={t('publicPage.cpf')}
                value={form.cpf}
                inputMode="numeric"
                placeholder="000.000.000-00"
                onChange={(e) => setForm((f) => ({ ...f, cpf: maskCpf(e.target.value) }))}
              />
            )}

            {!needsAccount && !needsCpf && <div className={styles.hint}>{t('publicPage.confirmHint')}</div>}

            {err && <span className={styles.err}>{err}</span>}
            <Button loading={submitting} onClick={checkout} style={{ width: '100%' }}>
              {t('publicPage.payCta', { price: fmtMoney(buying.price) })}
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
